import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { FadeUp, Overline, MaskedLines } from "@/components/Reveal";
import { IMG } from "@/data/site";

export default function Intro() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["-6%", "6%"]);

  return (
    <section data-testid="intro-section" className="py-28 md:py-40 overflow-hidden">
      <div className="max-w-[1500px] mx-auto px-6 md:px-12 grid lg:grid-cols-12 gap-12 lg:gap-8 items-start">
        <div className="lg:col-span-5 lg:pt-16">
          <FadeUp>
            <Overline>The First Impression</Overline>
          </FadeUp>
          <h2 className="mt-8 font-display font-light text-4xl md:text-5xl lg:text-6xl leading-[1.02] tracking-tight">
            <MaskedLines lines={["YOUR IMAGES ARE", "THE FIRST", "IMPRESSION."]} />
          </h2>
          <FadeUp delay={0.25} className="mt-10 space-y-6 text-sm md:text-base text-ivory/70 font-light leading-relaxed max-w-md">
            <p>
              In luxury real estate, the image is more than a photograph. It is the
              first emotional connection between a property and its future buyer.
            </p>
            <p>
              Our post-production process preserves the photographer&rsquo;s vision while
              refining every detail — light, colour, perspective, atmosphere and balance.
            </p>
            <p className="text-ivory font-display italic text-lg md:text-xl">
              The result is imagery that feels natural, polished and unmistakably premium.
            </p>
          </FadeUp>
        </div>

        <div ref={ref} className="lg:col-span-6 lg:col-start-7 relative">
          <FadeUp delay={0.15}>
            <div className="relative overflow-hidden border hairline">
              <motion.img
                style={{ y, scale: 1.12 }}
                src={IMG.intro}
                alt="Luxury real estate interior living room — professionally colour graded"
                className="w-full aspect-[4/5] object-cover"
                loading="lazy"
              />
            </div>
            <div className="absolute -bottom-5 -left-5 md:-left-10 bg-ink border border-gold/30 px-6 py-4">
              <p className="text-[10px] tracking-[0.3em] uppercase text-bronze">Precision in every frame</p>
            </div>
          </FadeUp>
        </div>
      </div>
    </section>
  );
}
